import { Injectable, OnDestroy } from '@angular/core';
import { Subject, race, timer } from 'rxjs';
import { take, map } from 'rxjs/operators';
import { environment } from 'projects/customer/src/environments/environment';

/**
 * User details sent to Next.js for OAuth token request
 */
export interface AuthUserDetails {
    tenant_id?: number;
    tenant_name?: string;
    tenant_email?: string;
    customer_id?: number;
    customer_name?: string;
    customer_email?: string;
    target_system?: 'OXM' | 'WMS' | 'OMS';
}

/**
 * Configuration object sent to Next.js Rule Engine
 */
export interface AuthConfig {
    logo: string;
    logoAlt: string;
    appName: string;
    tenantName: string;
    locale: string;
    preferences: Record<string, string>;
    labels: Record<string, string>;
}

/**
 * Message types for PostMessage communication
 */
export type AuthMessageType =
    | 'AUTH_READY'
    | 'AUTH_WITH_CONFIG'
    | 'AUTH_SUCCESS'
    | 'AUTH_ERROR';

/**
 * Structure of messages exchanged via PostMessage
 */
export interface AuthMessage {
    type: AuthMessageType;
    userDetails?: AuthUserDetails;
    config?: AuthConfig;
    error?: string;
}

/**
 * Result of authentication attempt
 */
export interface AuthResult {
    success: boolean;
    error?: string;
}

/**
 * Service to handle PostMessage-based authentication bridge
 * between Angular and Next.js Rule Engine application.
 *
 * Flow:
 * 1. Angular opens Next.js in a new tab (auth/loading page)
 * 2. Next.js sends AUTH_READY when ready to receive data
 * 3. Angular sends AUTH_WITH_CONFIG with userDetails and config
 * 4. Next.js requests OAuth token using userDetails, validates and sends AUTH_SUCCESS or AUTH_ERROR
 */
@Injectable({
    providedIn: 'root'
})
export class AuthBridgeService implements OnDestroy {
    private readonly nextJsOrigin: string;
    private messageListener: ((event: MessageEvent) => void) | null = null;
    private ruleEngineWindow: Window | null = null;
    private sendRetryInterval: ReturnType<typeof setInterval> | null = null;
    private pendingUserDetails: AuthUserDetails | null = null;
    private pendingConfig: AuthConfig | null = null;
    private messageSent = false;

    // Subjects for message events
    private authReady$ = new Subject<void>();
    private authSuccess$ = new Subject<void>();
    private authError$ = new Subject<string>();

    // Timeout for waiting for AUTH_READY (in milliseconds)
    private readonly AUTH_READY_TIMEOUT = 30000;
    // Interval for retrying to send auth data (in milliseconds)
    private readonly SEND_RETRY_INTERVAL = 1000;

    constructor() {
        this.nextJsOrigin = environment.ruleEngineUrl || 'http://localhost:3010';
        this.setupMessageListener();
    }

    ngOnDestroy(): void {
        this.cleanup();
    }

    /**
     * Opens the Next.js Rule Engine application and authenticates via PostMessage
     * @param userDetails User details for OAuth token request
     * @param config Authentication configuration object
     * @returns Promise that resolves with the auth result
     */
    async openAndAuthenticate(
        userDetails: AuthUserDetails,
        config: AuthConfig
    ): Promise<AuthResult> {
        return new Promise((resolve) => {
            // Open Next.js auth loading page in a new tab
            const loadingUrl = `${this.nextJsOrigin}/auth/loading`;
            this.ruleEngineWindow = window.open(loadingUrl, '_blank');

            if (!this.ruleEngineWindow) {
                resolve({
                    success: false,
                    error: 'Failed to open Rule Engine window. Please check your popup blocker settings.'
                });
                return;
            }

            // Wait for AUTH_READY signal from Next.js
            const readyOrTimeout$ = race(
                this.authReady$.pipe(
                    take(1),
                    map(() => ({ ready: true }))
                ),
                timer(this.AUTH_READY_TIMEOUT).pipe(
                    map(() => ({ ready: false, timeout: true }))
                )
            );

            readyOrTimeout$.subscribe((result) => {
                if (!result.ready) {
                    resolve({
                        success: false,
                        error: 'Timeout waiting for Rule Engine to be ready. Please try again.'
                    });
                    return;
                }

                // Send auth data to Next.js
                this.sendAuthData(userDetails, config);

                // Wait for success or error response
                const authResult$ = race(
                    this.authSuccess$.pipe(
                        take(1),
                        map(() => ({ success: true }))
                    ),
                    this.authError$.pipe(
                        take(1),
                        map((error) => ({ success: false, error }))
                    ),
                    timer(this.AUTH_READY_TIMEOUT).pipe(
                        map(() => ({
                            success: false,
                            error: 'Timeout waiting for authentication response.'
                        }))
                    )
                );

                authResult$.subscribe((authResult) => {
                    resolve(authResult as AuthResult);
                });
            });
        });
    }

    /**
     * Opens the Next.js Rule Engine and authenticates via OAuth
     * This method doesn't wait for AUTH_READY - it sends the data immediately
     * and retries periodically until success or timeout
     * @param userDetails User details for OAuth token request
     * @param config Authentication configuration object
     */
    openRuleEngine(userDetails: AuthUserDetails, config: AuthConfig): void {
        const loadingUrl = `${this.nextJsOrigin}/auth/loading`;

        // Store pending data
        this.pendingUserDetails = userDetails;
        this.pendingConfig = config;
        this.messageSent = false;

        // Clear any existing retry interval
        if (this.sendRetryInterval) {
            clearInterval(this.sendRetryInterval);
            this.sendRetryInterval = null;
        }

        // Open the window
        this.ruleEngineWindow = window.open(loadingUrl, '_blank');

        if (!this.ruleEngineWindow) {
            return;
        }

        // Strategy 1: Wait for AUTH_READY signal
        this.authReady$.pipe(take(1)).subscribe(() => {
            this.sendAuthDataToWindow();
        });

        // Strategy 2: Also send after a delay (in case AUTH_READY is missed)
        // This handles the case where window.opener is null in the new tab
        setTimeout(() => {
            if (!this.messageSent) {
                this.sendAuthDataToWindow();
            }
        }, 500);

        // Strategy 3: Retry periodically until we get AUTH_SUCCESS or AUTH_ERROR
        this.sendRetryInterval = setInterval(() => {
            if (!this.messageSent && this.ruleEngineWindow && !this.ruleEngineWindow.closed) {
                this.sendAuthDataToWindow();
            } else if (this.messageSent || !this.ruleEngineWindow || this.ruleEngineWindow.closed) {
                // Stop retrying
                if (this.sendRetryInterval) {
                    clearInterval(this.sendRetryInterval);
                    this.sendRetryInterval = null;
                }
            }
        }, this.SEND_RETRY_INTERVAL);

        // Listen for success/error to stop retrying
        this.authSuccess$.pipe(take(1)).subscribe(() => {
            this.messageSent = true;
            if (this.sendRetryInterval) {
                clearInterval(this.sendRetryInterval);
                this.sendRetryInterval = null;
            }
        });

        this.authError$.pipe(take(1)).subscribe(() => {
            this.messageSent = true;
            if (this.sendRetryInterval) {
                clearInterval(this.sendRetryInterval);
                this.sendRetryInterval = null;
            }
        });
    }

    /**
     * Sends the pending auth data to the Rule Engine window
     */
    private sendAuthDataToWindow(): void {
        if (!this.ruleEngineWindow || this.ruleEngineWindow.closed) {
            return;
        }

        if (!this.pendingUserDetails || !this.pendingConfig) {
            return;
        }

        const message: AuthMessage = {
            type: 'AUTH_WITH_CONFIG',
            userDetails: this.pendingUserDetails,
            config: this.pendingConfig
        };

        try {
            // Send to specific origin for security
            this.ruleEngineWindow.postMessage(message, this.nextJsOrigin);
        } catch (e) {
            // Silent fail - retry will handle it
        }
    }

    /**
     * Sends user details and config to Next.js via PostMessage
     * @param userDetails User details for OAuth token request
     * @param config Authentication configuration object
     */
    private sendAuthData(userDetails: AuthUserDetails, config: AuthConfig): void {
        this.pendingUserDetails = userDetails;
        this.pendingConfig = config;
        this.sendAuthDataToWindow();
    }

    /**
     * Sets up the message event listener to handle PostMessage communication
     */
    private setupMessageListener(): void {
        this.messageListener = (event: MessageEvent) => {
            // Validate origin - only accept messages from configured Next.js URL
            if (event.origin !== this.nextJsOrigin) {
                return;
            }

            const message = event.data as AuthMessage;

            // Validate message structure
            if (!message || typeof message.type !== 'string') {
                return;
            }

            switch (message.type) {
                case 'AUTH_READY':
                    this.authReady$.next();
                    break;
                case 'AUTH_SUCCESS':
                    this.authSuccess$.next();
                    break;
                case 'AUTH_ERROR':
                    this.authError$.next(message.error || 'Unknown error');
                    break;
            }
        };

        window.addEventListener('message', this.messageListener);
    }

    /**
     * Cleans up resources - removes event listener and closes window if needed
     */
    cleanup(): void {
        if (this.messageListener) {
            window.removeEventListener('message', this.messageListener);
            this.messageListener = null;
        }

        if (this.sendRetryInterval) {
            clearInterval(this.sendRetryInterval);
            this.sendRetryInterval = null;
        }

        // Complete subjects
        this.authReady$.complete();
        this.authSuccess$.complete();
        this.authError$.complete();

        // Clear pending data
        this.pendingUserDetails = null;
        this.pendingConfig = null;
    }

    /**
     * Closes the Rule Engine window if it's still open
     */
    closeRuleEngineWindow(): void {
        if (this.ruleEngineWindow && !this.ruleEngineWindow.closed) {
            this.ruleEngineWindow.close();
        }
        this.ruleEngineWindow = null;
    }

    /**
     * Gets the configured Next.js origin URL
     */
    getNextJsOrigin(): string {
        return this.nextJsOrigin;
    }
}
